import { useEffect, useRef, useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import Groq from "groq-sdk";
import axios from "axios";
import { baseUrl } from "../utils/OtherUtils";

const groq = new Groq({
	apiKey: "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",
	dangerouslyAllowBrowser: true,
});

const EIGHT_HOURS = 8 * 60 * 60 * 1000;
const MAX_QUESTIONS = 5;

function getRemainingTime() {
	const lockTime = localStorage.getItem("lockTime");
	if (!lockTime) return 0;
	const elapsed = Date.now() - parseInt(lockTime, 10);
	return Math.max(0, EIGHT_HOURS - elapsed);
}

const ChatBot = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [chatStep, setChatStep] = useState("initial");
	const [userMessages, setUserMessages] = useState([]);
	const [queryText, setQueryText] = useState("");
	const [name, setName] = useState("");
	const [number, setNumber] = useState("");
	const [callbackField, setCallbackField] = useState(true);
	const [isLoading, setIsLoading] = useState(false); // Loading state

	const messagesEndRef = useRef(null);

	// For question limit and lock
	const [questionCount, setQuestionCount] = useState(MAX_QUESTIONS);
	const [isLocked, setIsLocked] = useState(false);
	const [lockTimer, setLockTimer] = useState(getRemainingTime());

	// Sidebar hover
	const [showSidebar, setShowSidebar] = useState(false);

	// On mount: check lock and count
	useEffect(() => {
		const lockTime = localStorage.getItem("lockTime");
		if (lockTime) {
			const remaining = getRemainingTime();
			if (remaining > 0) {
				setIsLocked(true);
				setLockTimer(remaining);
				setQuestionCount(0);
			} else {
				setIsLocked(false);
				setQuestionCount(MAX_QUESTIONS);
				localStorage.removeItem("lockTime");
				localStorage.setItem("questionCount", MAX_QUESTIONS.toString());
			}
		} else {
			const storedCount = localStorage.getItem("questionCount");
			setQuestionCount(storedCount ? parseInt(storedCount, 10) : MAX_QUESTIONS);
		}
	}, []);

	// Lock timer countdown
	useEffect(() => {
		if (isLocked && lockTimer > 0) {
			const interval = setInterval(() => {
				setLockTimer((prev) => {
					if (prev <= 1000) {
						setIsLocked(false);
						setQuestionCount(MAX_QUESTIONS);
						localStorage.removeItem("lockTime");
						localStorage.setItem("questionCount", MAX_QUESTIONS.toString());
						return 0;
					}
					return prev - 1000;
				});
			}, 1000);
			return () => clearInterval(interval);
		}
	}, [isLocked, lockTimer]);

	// Sidebar hover logic
	const handleIconMouseEnter = () => setShowSidebar(true);
	const handleIconMouseLeave = () => setShowSidebar(false);

	const handleQuerySubmit = async (e) => {
		e.preventDefault();
		if (queryText.length === 0) return;
		if (queryText.trim().split(/\s+/).length > 50) return;
		if (isLocked || questionCount <= 0) return;

		setIsLoading(true); // Start loading

		const newCount = questionCount - 1;
		setQuestionCount(newCount);
		localStorage.setItem("questionCount", newCount.toString());

		if (newCount === 0) {
			setIsLocked(true);
			const now = Date.now();
			localStorage.setItem("lockTime", now.toString());
			setLockTimer(EIGHT_HOURS);
		}

		const currentMessages = [
			...userMessages,
			{ text: queryText.trim(), from: "user" },
		];
		setUserMessages(currentMessages);
		setQueryText("");

		try {
			const response = await getGroqChatCompletion(queryText);
			const reply = response.choices[0].message.content;
			const parsedReply = typeof reply === "string" ? JSON.parse(reply) : reply;

			// Handle contact details specifically
			if (parsedReply.contact) {
				setUserMessages([
					...currentMessages,
					{ contact: parsedReply.contact, from: "ai" },
				]);
			}
			// Handle address
			else if (parsedReply.address) {
				setUserMessages([
					...currentMessages,
					{ text: parsedReply.address, from: "ai" },
				]);
			}
			// Handle about Fine Labs
			else if (parsedReply.about) {
				setUserMessages([
					...currentMessages,
					{ about: parsedReply.about, from: "ai" },
				]);
			}
			// Handle packages list
			else if (parsedReply.packages) {
				setUserMessages([
					...currentMessages,
					{ packages: parsedReply.packages, from: "ai" },
				]);
			}
			// Handle timings
			else if (parsedReply.timings) {
				setUserMessages([
					...currentMessages,
					{ timings: parsedReply.timings, from: "ai" },
				]);
			}
			// Handle regular message
			else if (parsedReply.message) {
				setUserMessages([
					...currentMessages,
					{ text: parsedReply.message, from: "ai" },
				]);
			}
			// Handle test data
			else if (parsedReply) {
				// Only include present, non-empty keys
				const testData = {};
				if (parsedReply.productDescription)
					testData.productDescription = parsedReply.productDescription;
				if (parsedReply.recommendedFor)
					testData.recommendedFor = parsedReply.recommendedFor;
				if (parsedReply.parametersCovered)
					testData.parametersCovered = parsedReply.parametersCovered;
				if (parsedReply.reportTime)
					testData.reportTime = parsedReply.reportTime;
				if (parsedReply.alsoKnownAs && parsedReply.alsoKnownAs.length > 0)
					testData.alsoKnownAs = parsedReply.alsoKnownAs;
				if (parsedReply.cost) testData.cost = parsedReply.cost;

				if (Object.keys(testData).length > 0) {
					// try {
					// 	await axios.post(
					// 		`${baseUrl}/api/productDetails/add-productDetails`,
					// 		{
					// 			...testData,
					// 			question: queryText,
					// 		}
					// 	);
					// } catch (error) {
					// 	// Ignore DB error
					// }
					setUserMessages([...currentMessages, { testData, from: "ai" }]);
				} else {
					setUserMessages([
						...currentMessages,
						{
							text: "Sorry, I do not have this information. Please call 9138923900 or email support@finelabs.in for assistance.",
							from: "ai",
						},
					]);
				}
			} else {
				setUserMessages([
					...currentMessages,
					{
						text: "Sorry, I do not have this information. Please call 9138923900 or email support@finelabs.in for assistance.",
						from: "ai",
					},
				]);
			}
		} catch (error) {
			console.error("Error processing request:", error);
			setUserMessages([
				...currentMessages,
				{
					text: "Sorry, I encountered an error processing your request.",
					from: "ai",
				},
			]);
		} finally {
			setIsLoading(false); // Stop loading
		}
	};

	const handleCallbackSubmit = async (e) => {
		e.preventDefault();
		if (!name || !number) return;

		setUserMessages((prevMessages) => [
			...prevMessages,
			{ text: `Name: ${name} Number: ${number}`, from: "user" },
		]);
		const tempName = name;
		const tempNumber = number;
		setName("");
		setNumber("");
		setCallbackField(false);

		try {
			await axios.post(`${baseUrl}/api/callbackRequest/add-callbackRequest`, {
				name,
				number,
			});
			setUserMessages((prevMessages) => [
				...prevMessages,
				{
					text: "We've received your details. Our team will reach out to you shortly!",
					from: "ai",
				},
			]);
			setTimeout(() => {
				setName("");
				setNumber("");
				setUserMessages([]);
				setChatStep("initial");
			}, 2000);
		} catch (error) {
			setUserMessages([]);
			setName(tempName);
			setNumber(tempNumber);
			setCallbackField(false);
		}
	};

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [userMessages, chatStep]);

	const handleBack = () => {
		setUserMessages([]);
		setChatStep("initial");
		setCallbackField(true);
		setQueryText("");
		setName("");
		setNumber("");
	};

	const formatLabName = (labKey) => {
		if (labKey === "pathkindLabs") return "Pathkind Labs";
		if (labKey === "redcliffeLabs") return "Redcliffe Labs";
		if (labKey === "agilus") return "Agilus";
		if (labKey === "metropolis") return "Metropolis";
		if (labKey === "visitCentre") return "Visit Centre";
		return labKey
			.replace(/([A-Z])/g, " $1")
			.replace(/^./, (str) => str.toUpperCase());
	};

	const getGroqChatCompletion = async (queryText) => {
		return groq.chat.completions.create({
			messages: [
				{
					role: "system",
					content: `
You are Fine AI, a specialized assistant that ONLY answers questions about medical tests, health packages, lab timings, Fine Labs information, address, and contact details.

**CORE RULES:**
- Always provide complete, accurate answers - NEVER give empty, null, or "N/A" responses
- Always reply in valid JSON format ONLY
- Answer exactly what the user asks for without adding unrelated information
- If you don't know specific details, use the fallback messages provided below

**1. MEDICAL TEST/PACKAGE DETAILS:**
When user asks about a specific test or package (e.g., "What is CBC?", "thyroid package details", "LFT test"):
- Reply with ALL available information in this JSON format:
{
  "productDescription": "Clear description of what the test/package includes",
  "recommendedFor": { "age": "age range", "gender": "gender specification" },
  "parametersCovered": "List of all parameters/tests included",
  "reportTime": "Time to get results",
  "alsoKnownAs": ["alternative names"],
  "cost": "Cost starts from [minimum value]"
}
- ONLY include fields you have information for - omit unknown fields completely
- If user asks ONLY for cost: { "cost": "Cost starts from [amount]" }

**2. COST INQUIRIES:**
- If you don't know the cost: 
{ "message": "Sorry, I do not have this information. Please try searching the product name in the search bar. If you still do not get the correct answer, please call 9138923900 or email support@finelabs.in for assistance." }

**3. FINE LABS PACKAGES LIST:**
When user asks for "packages", "test list", "available tests":
{ "packages": ["Complete Health Checkup", "Diabetes Package", "Thyroid Profile", "Liver Function Test", "Kidney Function Test", "Cardiac Risk Markers", "Vitamin Profile", "Iron Deficiency Package", "Pregnancy Package", "Senior Citizen Package", "Executive Health Package", "Basic Health Package", "Women's Health Package", "Men's Health Package"] }

**4. LAB TIMINGS:**
For specific lab timings (e.g., "pathkind timings", "visit centre timings"):
{ "timings": { "labName": ["time slots"] } }

For all lab timings:
{
  "timings": {
    "pathkindLabs": ["7 AM - 8 AM", "8 AM - 9 AM", "9 AM - 10 AM", "10 AM - 11 AM", "11 AM - 12 PM", "12 PM - 1 PM", "1 PM - 2 PM", "2 PM - 3 PM", "3 PM - 4 PM"],
    "redcliffeLabs": ["5 AM - 6 AM", "6 AM - 7 AM", "7 AM - 8 AM", "8 AM - 9 AM", "9 AM - 10 AM", "10 AM - 11 AM", "11 AM - 12 PM", "12 PM - 1 PM", "1 PM - 2 PM", "2 PM - 3 PM"],
    "agilus": ["6 AM - 7 AM", "7 AM - 8 AM", "8 AM - 9 AM", "9 AM - 10 AM", "10 AM - 11 AM", "11 AM - 12 PM", "12 PM - 1 PM", "1 PM - 2 PM", "2 PM - 3 PM", "3 PM - 4 PM", "4 PM - 5 PM", "5 PM - 6 PM"],
    "metropolis": ["7 AM - 8 AM", "8 AM - 9 AM", "9 AM - 10 AM", "10 AM - 11 AM", "11 AM - 12 PM", "12 PM - 1 PM", "1 PM - 2 PM", "2 PM - 3 PM"],
    "visitCentre": ["10 AM - 11 AM", "11 AM - 12 AM", "12 AM - 01 PM", "01 PM - 02 PM", "02 PM - 03 PM", "03 PM - 04 PM", "04 PM - 05 PM", "05 PM - 06 PM", "06 PM - 07 PM"]
  }
}

**5. CONTACT DETAILS:**
For any contact-related query (contact, contact details, phone number, number, call, etc.):
{ "contact": { "number": "9138923900", "email": "support@finelabs.in" } }

**6. ADDRESS:**
{ "address": "Online delivery available in Gurugram, Haryana, Delhi, and across India." }

**7. ABOUT FINE LABS:**
{
  "about": [
    "India's Best Lab Bazaar for health diagnostics and convenience.",
    "Partners with top Indian labs: Agilus Diagnostics, Pathkind Labs, Redcliffe Labs, Metropolis Healthcare, and more.",
    "Provides affordable, accurate, and timely health checkups with a premium experience.",
    "Offers medical lab testing with home sample collection across India, with free and prompt report delivery.",
    "Simple process: 1. Explore tests, 2. Book online, 3. Home or lab sample collection, 4. Reports delivered via website.",
    "Customer-centric approach focused on convenience, quality, and satisfaction.",
    "Extensive menu of diagnostic tests and health packages.",
    "Trusted by millions for reliable, high-quality diagnostics."
  ]
}

**8. GENERAL HEALTH INFORMATION:**
For basic health questions (e.g., "What is diabetes?", "symptoms of anemia"):
{ "message": "Brief, helpful answer in 1-2 sentences with medical accuracy" }

**9. UNKNOWN INFORMATION:**
If you don't know other medical information:
{ "message": "Sorry, I do not have this information. Please call 9138923900 or email support@finelabs.in for assistance." }

**10. NON-MEDICAL QUERIES:**
For non-medical questions:
{ "message": "Hi, I'm Fine AI! I can only assist you with information about medical tests and packages." }

**CRITICAL REQUIREMENTS:**
- Always provide complete answers when information is available
- Never leave fields empty or use null/N/A values
- Always respond in valid JSON format
- No text outside JSON structure
- Prioritize being helpful while staying within scope
        `,
				},
				{
					role: "user",
					content: queryText,
				},
			],
			model: "llama-3.3-70b-versatile",
			response_format: { type: "json_object" },
		});
	};

	return (
		<>
			{/* Chatbot Icon */}
			<div
				className='fixed bg-[#0047AF] z-[40] w-[60px] h-[60px] right-[20px] bottom-[100px] lg:right-10 lg:bottom-10 rounded-tl-full rounded-tr-full rounded-bl-full flex items-center justify-center shadow-[0_0_20px_rgba(0,0,0,0.3)] cursor-pointer'
				onClick={() => {
					setIsOpen(!isOpen);
					if (chatStep === "callback") {
						setName("");
						setNumber("");
						setUserMessages([]);
						setChatStep("initial");
					}
				}}
				onMouseEnter={handleIconMouseEnter}
				onMouseLeave={handleIconMouseLeave}>
				<div>
					{isOpen ? (
						<img
							src='/assets/fineAi-close-icon.png'
							alt=''
							width={14}
							height={14}
						/>
					) : (
						<img src='/assets/fineAi-icon.png' alt='' width={32} height={32} />
					)}
				</div>
			</div>

			{/* Sidebar on hover */}
			{showSidebar && !isOpen && (
				<div
					className='fixed right-[50px] bottom-[100px] lg:right-[70px] lg:bottom-22 pr-10 flex items-center'
					style={{
						background:
							"linear-gradient(90deg,rgb(4, 19, 91) 0%,#2674E9  100%)",
						color: "#fff",
						borderRadius: "24px",
						padding: "16px 24px",
						boxShadow: "0 4px 24px rgba(0,188,212,0.15)",
						zIndex: 50,
						minWidth: "240px",
						fontWeight: 600,
						fontSize: "1rem",
					}}>
					How can I assist you... just tap me.
				</div>
			)}

			{/* Chatbot Window */}
			<div
				className={`fixed bottom-[180px] lg:bottom-28 lg:right-10 w-full lg:w-[400px] h-[450px] bg-white shadow-xl rounded-3xl flex flex-col transition-all duration-500 ease-in-out transform ${
					isOpen ? "opacity-100 scale-100" : "opacity-0 scale-0"
				}`}
				style={{ zIndex: 40 }}>
				<div className='p-4 font-bold border-b flex items-center gap-2 text-[#0047AF]'>
					{(chatStep === "query" || chatStep === "callback") && (
						<button
							onClick={handleBack}
							className='text-gray-600 hover:text-black'>
							<IoIosArrowBack size={18} />
						</button>
					)}
					Chat with Fine AI...
				</div>
				<div className='flex-1 overflow-auto p-4 space-y-2 text-sm bg-gray-50'>
					{chatStep === "initial" && (
						<div className='text-gray-800'>Hello! How can I help you?</div>
					)}
					{chatStep === "query" && (
						<div className='text-gray-800'>
							Ask any questions regarding Fine Labs and the products.
						</div>
					)}
					{chatStep === "callback" && (
						<div className='text-gray-800'>
							Please provide name and number so that our team can reach out to
							you.
						</div>
					)}
					{userMessages.map((msg, idx) => (
						<div
							key={idx}
							className={`w-fit max-w-[80%] p-2 ${
								msg.from === "user"
									? "bg-[#0047AF] text-white self-end text-right ml-auto rounded-[16px]"
									: "bg-gray-200 self-start text-left rounded-[16px]"
							}`}>
							{/* Regular text message */}
							{msg.text && msg.text}

							{/* Contact details */}
							{msg.contact && (
								<div className='flex flex-col gap-1'>
									<div className='font-semibold'>Contact Details:</div>
									<div>Phone: {msg.contact.number}</div>
									<div>Email: {msg.contact.email}</div>
								</div>
							)}

							{/* About section */}
							{msg.about && (
								<div className='flex flex-col gap-1'>
									<div className='font-semibold'>About Fine Labs:</div>
									{msg.about.map((item, i) => (
										<div key={i} className='mb-1'>
											• {item}
										</div>
									))}
								</div>
							)}

							{/* Packages list */}
							{msg.packages && (
								<div className='flex flex-col gap-1'>
									<div className='font-semibold'>Available Packages:</div>
									{msg.packages.map((pkg, i) => (
										<div key={i} className='mb-1'>
											• {pkg}
										</div>
									))}
								</div>
							)}

							{/* Test data */}
							{msg.testData && (
								<div className='flex flex-col'>
									{msg.testData.productDescription && (
										<div className=' text-md'>
											{msg.testData.productDescription}
										</div>
									)}
									{msg.testData.recommendedFor && (
										<div className='mt-2'>
											<span className='font-semibold'>Recommended For:</span>{" "}
											{msg.testData.recommendedFor.age},{" "}
											{msg.testData.recommendedFor.gender}
										</div>
									)}
									{msg.testData.parametersCovered && (
										<div className='mt-2'>
											<span className='font-semibold'>Parameters Covered:</span>{" "}
											{msg.testData.parametersCovered}
										</div>
									)}
									{msg.testData.reportTime && (
										<div className='mt-2'>
											<span className='font-semibold'>Report Time:</span>{" "}
											{msg.testData.reportTime}
										</div>
									)}
									{msg.testData.alsoKnownAs &&
										msg.testData.alsoKnownAs.length > 0 && (
											<div className='mt-2'>
												<span className='font-semibold'>Also Known As:</span>{" "}
												{msg.testData.alsoKnownAs.join(", ")}
											</div>
										)}
									{msg.testData.cost && (
										<div className='mt-2'>
											<span className='font-semibold'>Cost:</span>{" "}
											{msg.testData.cost}
										</div>
									)}
								</div>
							)}

							{/* Timings */}
							{msg.timings && (
								<div className='mt-2'>
									<div className='font-semibold mb-2'>Lab Timings:</div>
									{Object.entries(msg.timings).map(([lab, slots], i) => (
										<div key={i} className='ml-1 mt-1'>
											<div className='font-medium'>{formatLabName(lab)}:</div>
											<div className='ml-1 flex flex-col gap-1'>
												{slots.map((slot, j) => (
													<div key={j}>{slot}</div>
												))}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					))}
					<div ref={messagesEndRef} />
					{chatStep === "initial" && (
						<div className='flex flex-col gap-2 items-end'>
							<button
								onClick={() => setChatStep("callback")}
								className='w-[180px] bg-blue-500 text-white px-4 py-2 rounded-[32px] hover:bg-[#0047AF] transition text-sm'>
								Request a Callback
							</button>
							<button
								onClick={() => setChatStep("query")}
								className='w-[180px] bg-gray-200 text-gray-800 px-4 py-2 rounded-[32px] hover:bg-gray-300 transition text-sm'>
								Ask a Query
							</button>
						</div>
					)}
				</div>

				{/* Query input and limit */}
				{chatStep === "query" && (
					<div className='p-3 border-t'>
						{isLocked || questionCount === 0 ? (
							<div className='flex flex-col items-center text-red-500'>
								<p>You've reached the limit of 5 questions.</p>
								<p>
									Please wait {Math.floor(lockTimer / 3600000)}h{" "}
									{Math.floor((lockTimer % 3600000) / 60000)}m to ask again.
								</p>
							</div>
						) : (
							<>
								<form onSubmit={handleQuerySubmit} className='flex gap-2'>
									<div className='border border-gray-300 rounded-3xl w-full flex justify-between'>
										<input
											type='text'
											value={queryText}
											onChange={(e) => setQueryText(e.target.value)}
											placeholder='Type your query...'
											className='flex-1 text-sm outline-none rounded-3xl pl-2 py-2 pr-2'
											disabled={isLoading}
										/>
										<button
											type='submit'
											disabled={isLoading}
											className={`outline-none rounded-3xl text-white px-6 py-3 text-sm transition-all duration-200 ${
												isLoading
													? "bg-gray-400 cursor-not-allowed"
													: "bg-gradient-to-r from-[#0047AF] to-[#1A67D8] hover:bg-blue-700"
											}`}>
											{isLoading ? (
												<div className='flex items-center space-x-2'>
													<div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent'></div>
													<span>Analyzing...</span>
												</div>
											) : (
												"Send"
											)}
										</button>
									</div>
								</form>
								<div className='text-xs text-center text-gray-500 mt-1'>
									{questionCount} questions remaining
								</div>
							</>
						)}
					</div>
				)}

				{/* Callback input */}
				{chatStep === "callback" && callbackField && (
					<div className='p-3 border-t'>
						<form onSubmit={handleCallbackSubmit} className='space-y-2'>
							<input
								type='text'
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder='Your Name'
								className='w-full border border-gray-300 rounded-3xl px-2 py-3 outline-none text-sm'
							/>
							<div className='flex flex-row w-full border border-gray-300 rounded-3xl align-items outline-none text-sm'>
								<div
									className='px-2 py-3 text-[#57636C] border-gray-300'
									style={{ borderRight: "1px solid #D1D5DB" }}>
									+91
								</div>
								<input
									type='tel'
									value={number}
									onChange={(e) => {
										const input = e.target.value;
										if (/^\d{0,10}$/.test(input)) {
											setNumber(input);
										}
									}}
									placeholder='Your Number'
									className='w-full rounded-3xl outline-none text-sm px-2 py-3'
								/>
							</div>
							<button
								type='submit'
								className='w-full bg-gradient-to-r outline-none from-[#0047AF] to-[#1A67D8] rounded-3xl text-white px-3 py-3  text-sm hover:bg-green-700'>
								Submit
							</button>
						</form>
					</div>
				)}
			</div>
		</>
	);
};

export default ChatBot;
